import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X, Loader2, DollarSign, Receipt, Paperclip } from "lucide-react";
import api, { fmtMXN, fmtDate, todayLocal, EXPENSE_PAYMENT_METHODS } from "@/lib/api";
import ReceiptViewer from "@/components/ReceiptViewer";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StampCfdiModal } from "@/components/StampCfdiModal";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const INV_STATUS = {
  pendiente: { label: "Pendiente", color: "#f59e0b" },
  parcial: { label: "Pago parcial", color: "#22d3ee" },
  pagada: { label: "Pagada", color: "#16a34a" },
  cancelada: { label: "Cancelada", color: "#dc2626" },
};

export default function ContractInvoices({ contract }) {
  const { user } = useAuth();
  const canWrite = ["admin", "contador", "auxiliar"].includes(user?.role);
  const canDelete = ["admin", "contador"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [estimaciones, setEstimaciones] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openInv, setOpenInv] = useState(false);
  const [editingInv, setEditingInv] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [viewer, setViewer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: sum }, { data: ests }] = await Promise.all([
        api.get(`/contracts/${contract.id}/client-invoices`),
        api.get(`/contracts/${contract.id}/estado-cuenta`),
        api.get(`/contracts/${contract.id}/estimaciones`),
      ]);
      setRows(data || []);
      setSummary(sum);
      setEstimaciones(ests || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract.id]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ci-refresh", h);
    return () => window.removeEventListener("ci-refresh", h);
    /* eslint-disable-next-line */
  }, [contract.id]);

  const invoiceAdvance = async () => {
    const pct = (contract.advances || []).reduce((s, a) => s + (a.pct || 0), 0);
    const amt = Math.round((contract.total || 0) * pct) / 100;
    if (!window.confirm(`¿Facturar el ANTICIPO del ${pct}% (${fmtMXN(amt)})? Se creará la factura con folio automático y formato IGIFE.`)) return;
    try {
      const { data } = await api.post(`/contracts/${contract.id}/client-invoices/from-advance`);
      toast.success(`Factura ${data.folio} de anticipo creada`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const [stampFor, setStampFor] = useState(null);
  const downloadCfdi = async (inv, kind) => {
    try {
      const res = await api.get(`/client-invoices/${inv.id}/cfdi/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cfdi_${inv.folio.replace(" ", "")}.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(`No se pudo descargar el ${kind.toUpperCase()}`); }
  };
  const cancelCfdi = async (inv) => {
    const motive = window.prompt(`Motivo de cancelación SAT para ${inv.folio}:\n01 = Error con relación (requiere UUID sustituto)\n02 = Error sin relación\n03 = No se llevó a cabo la operación\n04 = Operación nominativa`, "02");
    if (!motive) return;
    let substitution = null;
    if (motive === "01") {
      substitution = window.prompt("UUID de la factura que la sustituye:");
      if (!substitution) return;
    }
    try {
      await api.post(`/client-invoices/${inv.id}/cfdi-cancel`, { motive, substitution });
      toast.success("Cancelación enviada al SAT");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cancelar"); }
  };
  const emailCfdi = async (inv) => {
    const email = window.prompt(`${inv.emailed_at ? `Reenviar` : `Enviar`} factura ${inv.folio} (XML + PDF) por correo.${inv.emailed_at ? `\nÚltimo envío: ${inv.emailed_to} · ${fmtDT(inv.emailed_at)}` : ""}\nDeja vacío para usar el correo registrado del cliente:`, inv.emailed_to || "");
    if (email === null) return;
    try {
      const { data } = await api.post(`/client-invoices/${inv.id}/send-email`, { email: email || null });
      toast.success(`Factura enviada a ${data.sent_to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
  };
  const uploadXml = async (inv, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/client-invoices/${inv.id}/cfdi-xml`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`XML timbrado cargado · UUID …${(data.uuid_cfdi || "").slice(-8)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar el XML"); }
  };
  const downloadIgife = async (inv) => {
    try {
      const res = await api.get(`/client-invoices/${inv.id}/igife/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `FACTURA_${(((inv.sat || {}).serie_folio) || inv.folio).replace(/ /g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF IGIFE"); }
  };

  const removeInv = async (inv) => {
    if (!window.confirm(`¿Eliminar factura ${inv.folio}?`)) return;
    try { await api.delete(`/client-invoices/${inv.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const cancelInv = async (inv) => {
    if (!window.confirm(`¿Marcar CANCELADA la factura ${inv.folio}?`)) return;
    try { await api.patch(`/client-invoices/${inv.id}`, { status: "cancelada" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removePay = async (inv, pid) => {
    if (!window.confirm("¿Eliminar este pago?")) return;
    try { await api.delete(`/client-invoices/${inv.id}/payments/${pid}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const estLabel = (id) => {
    const e = estimaciones.find((x) => x.id === id);
    return e ? `Est. #${String(e.number).padStart(2, "0")}` : null;
  };

  return (
    <div data-testid="contract-invoices-section" className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-mono">// FACTURAS Y COBROS (ESTADO DE CUENTA DEL CLIENTE)</div>
          <div className="text-xs text-slate-400">{rows.length} factura{rows.length !== 1 ? "s" : ""}</div>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button data-testid="invoice-advance-btn" onClick={invoiceAdvance}
              className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2"
              style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
              <DollarSign className="w-3.5 h-3.5" /> Facturar anticipo
            </button>
            <button data-testid="new-client-invoice-btn" onClick={() => { setEditingInv(null); setOpenInv(true); }}
              className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Registrar factura
            </button>
          </div>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3" data-testid="estado-cuenta-summary">
          <Sum label="Contrato" value={fmtMXN(summary.contract_total)} />
          <Sum label="Estimado" value={fmtMXN(summary.estimado)} />
          <Sum label="Facturado" value={fmtMXN(summary.facturado)} />
          <Sum label="Cobrado" value={fmtMXN(summary.cobrado)} color="#16a34a" />
          <Sum label="Por cobrar" value={fmtMXN(summary.por_cobrar)} color="#f59e0b" />
          <Sum label="Por facturar" value={fmtMXN(summary.por_facturar)} color="#22d3ee" />
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-400 py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="panel p-6 text-center text-slate-400 text-sm">
          <Receipt className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          Sin facturas registradas. Registra las facturas que emites al cliente y sus pagos.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((inv) => {
            const st = INV_STATUS[inv.status] || INV_STATUS.pendiente;
            return (
              <div key={inv.id} className="panel" data-testid={`client-invoice-${inv.id}`}>
                <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-sm font-bold text-slate-100">{inv.folio}</span>
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest"
                    style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}66` }}
                    data-testid={`invoice-status-${inv.id}`}>{st.label}</span>
                  {inv.estimacion_id && estLabel(inv.estimacion_id) && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(34,211,238,0.1)", color: "var(--cyan)" }}>{estLabel(inv.estimacion_id)}</span>
                  )}
                  <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
                    {fmtDate(inv.invoice_date)}
                    {inv.uuid_cfdi && <span className="ml-2 font-mono" style={{ color: "#86efac" }}>UUID …{inv.uuid_cfdi.slice(-8)}{inv.cfdi_test_mode ? " · TEST" : ""}</span>}
                    {inv.concept && <span className="ml-2">· {inv.concept}</span>}
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-bold text-slate-100 tabular-nums">{fmtMXN(inv.total)}</div>
                    <div className="text-[10px]" style={{ color: inv.balance > 0.01 ? "#f59e0b" : "#16a34a" }}>
                      {inv.balance > 0.01 ? `saldo ${fmtMXN(inv.balance)}` : "cobrada al 100%"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canDelete && !inv.uuid_cfdi && inv.status !== "cancelada" && (
                      <button data-testid={`stamp-invoice-${inv.id}`} onClick={() => setStampFor(inv)}
                        title="Timbrar CFDI ante el SAT (Facturapi)" className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">
                        ⚡ Timbrar CFDI
                      </button>
                    )}
                    {(inv.facturapi_id || inv.sat || inv.igife) && (
                      <button data-testid={`cfdi-email-${inv.id}`} onClick={() => emailCfdi(inv)} title={inv.emailed_at ? `Enviada a ${inv.emailed_to}` : "Enviar factura (PDF IGIFE + XML) por correo"}
                        className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>✉ {inv.emailed_at ? "Reenviar" : "Enviar"}</button>
                    )}
                    {inv.facturapi_id && (
                      <>
                        <button data-testid={`cfdi-xml-${inv.id}`} onClick={() => downloadCfdi(inv, "xml")} title="XML timbrado"
                          className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#86efac", border: "1px solid #16a34a" }}>XML</button>
                        <button data-testid={`cfdi-pdf-${inv.id}`} onClick={() => downloadCfdi(inv, "pdf")} title="PDF timbrado"
                          className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#86efac", border: "1px solid #16a34a" }}>PDF SAT</button>
                        {canDelete && inv.status !== "cancelada" && (
                          <button data-testid={`cfdi-cancel-${inv.id}`} onClick={() => cancelCfdi(inv)} title="Cancelar CFDI ante el SAT"
                            className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#fca5a5", border: "1px solid #dc2626" }}>Cancelar CFDI</button>
                        )}
                      </>
                    )}
                    {canWrite && !inv.facturapi_id && inv.status !== "cancelada" && (
                      <label data-testid={`upload-xml-${inv.id}`} title={inv.sat ? "Reemplazar XML timbrado" : "Subir XML timbrado por tu PAC (SAT)"}
                        className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest cursor-pointer"
                        style={{ color: "#86efac", border: "1px solid #16a34a" }}>
                        {inv.sat ? "XML ✓" : "Subir XML"}
                        <input type="file" accept=".xml,text/xml" className="hidden"
                          onChange={(e) => { uploadXml(inv, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                    )}
                    {inv.igife && (
                      <button data-testid={`igife-pdf-${inv.id}`} onClick={() => downloadIgife(inv)}
                        title="PDF formato IGIFE" className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>
                        PDF IGIFE
                      </button>
                    )}
                    {inv.pdf_file_id && (
                      <button onClick={() => setViewer({ fileId: inv.pdf_file_id, title: `Factura ${inv.folio}` })}
                        title="Ver PDF de la factura" className="btn-ghost p-1.5"><Paperclip className="w-3.5 h-3.5" /></button>
                    )}
                    {canWrite && inv.status !== "cancelada" && inv.balance > 0.01 && (
                      <button data-testid={`add-payment-${inv.id}`} onClick={() => setPayFor(inv)}
                        className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">
                        <DollarSign className="w-3 h-3 inline mr-0.5" />Registrar cobro
                      </button>
                    )}
                    {canWrite && (
                      <button onClick={() => { setEditingInv(inv); setOpenInv(true); }} title="Editar" className="btn-ghost p-1.5 text-xs">✎</button>
                    )}
                    {canWrite && inv.status !== "cancelada" && (
                      <button onClick={() => cancelInv(inv)} title="Cancelar factura" className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}><X className="w-3.5 h-3.5" /></button>
                    )}
                    {canDelete && (
                      <button data-testid={`delete-invoice-${inv.id}`} onClick={() => removeInv(inv)} title="Eliminar" className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
                {inv.emailed_at && (
                  <div className="px-4 pb-2 -mt-1 text-[10px] font-mono flex items-center gap-2 flex-wrap" data-testid={`email-legend-${inv.id}`} style={{ color: "#86efac" }}>
                    ✉ Enviada a {inv.emailed_to} · {fmtDT(inv.emailed_at)}
                    {(inv.email_log || []).length > 1 && (
                      <span className="text-slate-500 cursor-help" title={inv.email_log.map((l) => `${l.to} · ${fmtDT(l.at)}${l.by ? ` · por ${l.by}` : ""}`).join("\n")}>
                        · {inv.email_log.length} envíos (ver historial)
                      </span>
                    )}
                  </div>
                )}
                {(inv.payments || []).length > 0 && (
                  <div className="border-t px-4 py-2" style={{ borderColor: "var(--border)", background: "rgba(15,23,42,0.4)" }}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Cobros ({inv.payments.length})</div>
                    {inv.payments.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 text-xs py-0.5">
                        <span className="font-mono text-slate-400">{fmtDate(p.payment_date)}</span>
                        <span className="font-mono font-bold" style={{ color: "#86efac" }}>{fmtMXN(p.amount)}</span>
                        <span className="text-slate-500">{EXPENSE_PAYMENT_METHODS.find((m) => m.value === p.method)?.label || p.method}</span>
                        {p.reference && <span className="text-slate-500 font-mono">ref {p.reference}</span>}
                        {p.receipt_file_id && (
                          <button onClick={() => setViewer({ fileId: p.receipt_file_id, title: "Comprobante de cobro" })} className="hover:underline" style={{ color: "var(--cyan)" }}>comprobante</button>
                        )}
                        <span className="flex-1" />
                        {canDelete && <button onClick={() => removePay(inv, p.id)} className="btn-ghost p-0.5" style={{ color: "#fca5a5" }}><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {openInv && (
        <InvoiceModal contract={contract} estimaciones={estimaciones} existing={editingInv}
          onClose={() => { setOpenInv(false); setEditingInv(null); }}
          onSaved={() => { setOpenInv(false); setEditingInv(null); load(); }} />
      )}
      {payFor && (
        <PaymentModal invoice={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }} />
      )}
      {stampFor && <StampCfdiModal inv={stampFor} onClose={() => setStampFor(null)} onStamped={() => { setStampFor(null); load(); }} />}
      {viewer && <ReceiptViewer fileId={viewer.fileId} title={viewer.title} onClose={() => setViewer(null)} />}
    </div>
  );
}

function Sum({ label, value, color }) {
  return (
    <div className="panel p-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono text-sm font-bold tabular-nums" style={{ color: color || "#e2e8f0" }}>{value}</div>
    </div>
  );
}

function InvoiceModal({ contract, estimaciones, existing, onClose, onSaved }) {
  const [form, setForm] = useState(() => existing ? { ...existing } : {
    estimacion_id: "", folio: "", uuid_cfdi: "", invoice_date: todayLocal(), total: "", concept: "", notes: "",
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const pickEst = (id) => {
    const e = estimaciones.find((x) => x.id === id);
    setForm((f) => ({
      ...f, estimacion_id: id,
      total: e ? e.total_period : f.total,
      concept: e ? `Estimación #${String(e.number).padStart(2, "0")} · ${fmtDate(e.period_start)} a ${fmtDate(e.period_end)}` : f.concept,
    }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      let pdfId = form.pdf_file_id || null;
      if (pdfFile) {
        const fd = new FormData();
        fd.append("file", pdfFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        pdfId = data.id;
      }
      const payload = {
        estimacion_id: form.estimacion_id || null,
        folio: form.folio,
        uuid_cfdi: form.uuid_cfdi || null,
        invoice_date: form.invoice_date,
        total: Math.round(Number(form.total) * 100) / 100,
        concept: form.concept || null,
        notes: form.notes || null,
        pdf_file_id: pdfId,
      };
      if (existing) {
        await api.patch(`/client-invoices/${existing.id}`, payload);
        toast.success("Factura actualizada");
      } else {
        await api.post(`/contracts/${contract.id}/client-invoices`, payload);
        toast.success("Factura registrada");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg panel-strong max-h-[92vh] overflow-y-auto p-5 space-y-3" data-testid="client-invoice-modal">
        <div className="label-mono">// {existing ? "EDITAR" : "REGISTRAR"} FACTURA AL CLIENTE</div>
        <div>
          <label className="label-mono block mb-1">Vincular a estimación (opcional)</label>
          <select value={form.estimacion_id || ""} onChange={(e) => pickEst(e.target.value)} className="input-cyber px-3 py-2 text-sm w-full" data-testid="invoice-estimacion-select">
            <option value="" style={{ background: "#0a0f1d" }}>— Sin vincular —</option>
            {estimaciones.map((e) => (
              <option key={e.id} value={e.id} style={{ background: "#0a0f1d" }}>Est. #{String(e.number).padStart(2, "0")} · {fmtMXN(e.total_period)}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-mono block mb-1">Folio *</label>
            <input required type="text" value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" data-testid="invoice-folio-input" /></div>
          <div><label className="label-mono block mb-1">Fecha *</label>
            <input required type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" data-testid="invoice-date" /></div>
          <div><label className="label-mono block mb-1">Total (con IVA) *</label>
            <input required type="number" step="0.01" min="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full text-right font-mono" data-testid="invoice-total" /></div>
          <div><label className="label-mono block mb-1">UUID CFDI (folio fiscal)</label>
            <input value={form.uuid_cfdi || ""} onChange={(e) => setForm({ ...form, uuid_cfdi: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full font-mono" placeholder="opcional" /></div>
        </div>
        <div><label className="label-mono block mb-1">Concepto</label>
          <input value={form.concept || ""} onChange={(e) => setForm({ ...form, concept: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
        <div><label className="label-mono block mb-1">PDF de la factura (opcional)</label>
          <input type="file" accept="application/pdf,image/*" onChange={(e) => setPdfFile(e.target.files[0])} className="input-cyber px-3 py-2 text-xs w-full" data-testid="invoice-pdf-input" />
          {form.pdf_file_id && !pdfFile && <div className="text-[10px] text-slate-500 mt-1">Ya tiene un PDF adjunto; sube otro para reemplazarlo.</div>}</div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="save-invoice-btn" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: invoice.balance, payment_date: todayLocal(), method: "transferencia", reference: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      let rid = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        rid = data.id;
      }
      await api.post(`/client-invoices/${invoice.id}/payments`, {
        amount: Math.round(Number(form.amount) * 100) / 100,
        payment_date: form.payment_date,
        method: form.method,
        reference: form.reference || null,
        receipt_file_id: rid,
      });
      toast.success("Cobro registrado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md panel-strong p-5 space-y-3" data-testid="invoice-payment-modal">
        <div className="label-mono">// REGISTRAR COBRO · FACTURA {invoice.folio}</div>
        <div className="text-xs text-slate-400">Saldo pendiente: <b className="font-mono" style={{ color: "#f59e0b" }}>{fmtMXN(invoice.balance)}</b></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-mono block mb-1">Monto *</label>
            <input required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full text-right font-mono" data-testid="payment-amount" /></div>
          <div><label className="label-mono block mb-1">Fecha *</label>
            <input required type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div><label className="label-mono block mb-1">Forma de pago</label>
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
              {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value} style={{ background: "#0a0f1d" }}>{m.label}</option>)}
            </select></div>
          <div><label className="label-mono block mb-1">Referencia</label>
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full font-mono" placeholder="# transferencia" /></div>
        </div>
        <div><label className="label-mono block mb-1">Comprobante (opcional)</label>
          <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} className="input-cyber px-3 py-2 text-xs w-full" /></div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="save-invoice-payment-btn" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50">
            {saving ? "Guardando…" : "Registrar cobro"}
          </button>
        </div>
      </form>
    </div>
  );
}
